"use client"

import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { Search, Filter, Users, MoreVertical, ChevronDown, UserCheck, UserX, UserPlus, Download, RefreshCcw, X } from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu.jsx'
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from '../ui/sheet.jsx'
import { VisuallyHidden } from '../ui/visually-hidden.jsx'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

// Performance optimized Team Directory component
export function TeamDirectory() {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({
    role: '',
    status: '',
    level: ''
  })
  const [selectedMembers, setSelectedMembers] = useState(new Set())
  const [showBulkBar, setShowBulkBar] = useState(false)
  const [bulkLoading, setBulkLoading] = useState(false)
  const [error, setError] = useState('')
  const [stats, setStats] = useState({ total: 0, active: 0, invited: 0 })

  // Debounced search for performance
  const [searchDebounce, setSearchDebounce] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(search)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // Load team members with performance optimization
  const loadMembers = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      
      const params = new URLSearchParams()
      if (searchDebounce) params.append('search', searchDebounce)
      if (filters.role) params.append('role', filters.role)
      if (filters.status) params.append('status', filters.status)
      if (filters.level) params.append('level', filters.level)
      params.append('limit', '50')

      const { data } = await axios.get(`${API}/admin/team/members?${params}`, { withCredentials: true })
      setMembers(data.items || [])
      
      // Calculate stats
      const total = data.items?.length || 0
      const active = data.items?.filter(m => m.status === 'active').length || 0
      const invited = data.items?.filter(m => m.status === 'invited').length || 0
      setStats({ total, active, invited })
      
    } catch (err) {
      setError('Failed to load team members')
      console.error('Team directory error:', err)
    } finally {
      setLoading(false)
    }
  }, [searchDebounce, filters])

  useEffect(() => {
    loadMembers()
  }, [loadMembers])

  // Handle member selection
  const toggleMemberSelection = (memberId) => {
    const newSelected = new Set(selectedMembers)
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId)
    } else {
      newSelected.add(memberId)
    }
    setSelectedMembers(newSelected)
    setShowBulkBar(newSelected.size > 0)
  }

  const selectAllMembers = () => {
    if (selectedMembers.size === members.length) {
      setSelectedMembers(new Set())
      setShowBulkBar(false)
    } else {
      const allIds = new Set(members.map(m => m.id))
      setSelectedMembers(allIds)
      setShowBulkBar(true)
    }
  }

  // Bulk actions
  const performBulkAction = async (action, targetRoleId = null) => {
    try {
      setBulkLoading(true)
      
      const payload = {
        action,
        member_ids: Array.from(selectedMembers),
        justification: `Bulk ${action} operation performed by admin`,
        ...(targetRoleId && { target_role_id: targetRoleId })
      }

      const { data } = await axios.post(`${API}/admin/team/members/bulk`, payload, { withCredentials: true })
      
      // Clear selections and reload
      setSelectedMembers(new Set())
      setShowBulkBar(false)
      await loadMembers()
      
      alert(`Bulk action completed. Job ID: ${data.job_id}`)
      
    } catch (err) {
      setError(`Bulk action failed: ${err.response?.data?.detail || err.message}`)
    } finally {
      setBulkLoading(false)
    }
  }

  // Filter options (these would come from RBAC system)
  const roleOptions = [
    { value: '', label: 'All Roles' },
    { value: 'subscriber', label: 'Subscriber' },
    { value: 'support_agent', label: 'Support Agent' },
    { value: 'support_lead', label: 'Support Lead' },
    { value: 'administrator', label: 'Administrator' },
    { value: 'organization_owner', label: 'Organization Owner' },
    { value: 'super_admin', label: 'Super Admin' }
  ]

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'invited', label: 'Invited' },
    { value: 'deactivated', label: 'Deactivated' }
  ]

  return (
    <div className="team-directory mobile-full-width">
      {/* Header with stats - Mobile Responsive */}
      <div className="flex-col-mobile items-start justify-between mb-6 mobile-full-width">
        <div className="w-full mobile-full-width">
          <h2 className="text-2xl font-semibold text-primary mb-2">Team Directory</h2>
          <div className="flex-col-mobile gap-4 text-sm text-muted mobile-full-width">
            <div className="flex gap-4 flex-wrap mobile-full-width">
              <span><Users size={16} className="inline mr-1" />{stats.total} Total</span>
              <span><UserCheck size={16} className="inline mr-1 text-green-600" />{stats.active} Active</span>
              <span><UserPlus size={16} className="inline mr-1 text-blue-600" />{stats.invited} Invited</span>
            </div>
          </div>
        </div>
        <button 
          onClick={loadMembers} 
          className="btn secondary w-full sm:w-auto mobile-full-width"
          disabled={loading}
          aria-label="Refresh team directory"
        >
          <RefreshCcw size={16} className={loading ? 'animate-spin' : ''} aria-hidden="true" />
          <span className="sm:inline">Refresh</span>
          <VisuallyHidden>
            {loading ? 'Refreshing team directory data' : 'Reload team member data from server'}
          </VisuallyHidden>
        </button>
      </div>

      {/* Search and Filters - Mobile Responsive */}
      <div className="flex-col-mobile gap-3 mb-6 mobile-full-width">
        <div className="flex-1 relative w-full mobile-full-width">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted" size={16} aria-hidden="true" />
          <input
            type="text"
            placeholder="Search team members..."
            className="input pl-10 w-full mobile-full-width"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search team members by name or email"
          />
          <VisuallyHidden>
            Search through team member names, emails, and roles. Results will update automatically as you type.
          </VisuallyHidden>
        </div>
        
        <div className="flex gap-2 w-full sm:w-auto mobile-full-width">
          <select
            className="input flex-1 sm:min-w-32 mobile-full-width"
            value={filters.role}
            onChange={(e) => setFilters({...filters, role: e.target.value})}
            aria-label="Filter by role"
          >
            {roleOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          
          <select
            className="input flex-1 sm:min-w-32 mobile-full-width"
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
            aria-label="Filter by status"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <VisuallyHidden>
            Use the dropdown filters to narrow down the team member list by role and account status.
          </VisuallyHidden>
        </div>
      </div>

      {/* Bulk Action Bar - Mobile Responsive */}
      {showBulkBar && (
        <div className="bulk-action-bar mb-4 p-4 bg-primary-50 border border-primary-200 rounded-lg mobile-full-width">
          <div className="flex-col-mobile items-start justify-between gap-3 mobile-full-width">
            <span className="font-medium text-responsive">
              {selectedMembers.size} member{selectedMembers.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2 flex-wrap w-full sm:w-auto mobile-full-width">
              <button
                onClick={() => performBulkAction('deactivate')}
                className="btn secondary text-xs flex-1 sm:flex-initial"
                disabled={bulkLoading}
              >
                <UserX size={14} />
                <span className="sm:inline">Deactivate</span>
              </button>
              <button
                onClick={() => performBulkAction('reactivate')}
                className="btn secondary text-xs flex-1 sm:flex-initial"
                disabled={bulkLoading}
              >
                <UserCheck size={14} />
                <span className="sm:inline">Reactivate</span>
              </button>
              <button
                onClick={() => setSelectedMembers(new Set())}
                className="btn secondary text-xs w-full sm:w-auto"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="error mb-4" role="alert">
          {error}
        </div>
      )}

      {/* Members List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-pulse">Loading team members...</div>
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-8">
          <Users size={48} className="mx-auto text-muted mb-4" />
          <p className="text-muted">No team members found</p>
        </div>
      ) : (
        <>
          {/* Selection Header */}
          <div className="flex items-center gap-3 mb-4 pb-2 border-b border-muted-200">
            <input
              type="checkbox"
              className="rounded"
              checked={selectedMembers.size === members.length && members.length > 0}
              onChange={selectAllMembers}
            />
            <span className="text-sm text-muted">
              {selectedMembers.size > 0 ? `${selectedMembers.size} selected` : 'Select all'}
            </span>
          </div>

          {/* Member Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {members.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                selected={selectedMembers.has(member.id)}
                onSelect={() => toggleMemberSelection(member.id)}
                onRefresh={loadMembers}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// Individual Member Card Component
function MemberCard({ member, selected, onSelect, onRefresh }) {
  const [actionLoading, setActionLoading] = useState(false)
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [showProfileModal, setShowProfileModal] = useState(false)

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600'
      case 'invited': return 'text-blue-600'
      case 'deactivated': return 'text-red-600'
      default: return 'text-muted'
    }
  }

  const getHierarchyBadge = (level) => {
    if (level <= 2) return 'bg-red-100 text-red-800'
    if (level <= 5) return 'bg-orange-100 text-orange-800'
    if (level <= 7) return 'bg-blue-100 text-blue-800'
    return 'bg-gray-100 text-gray-800'
  }

  const performMemberAction = async (action) => {
    try {
      setActionLoading(true)
      
      let endpoint = ''
      let payload = {}
      
      if (action === 'deactivate' || action === 'reactivate') {
        endpoint = `${API}/admin/team/members/${member.id}`
        payload = { status: action === 'deactivate' ? 'deactivated' : 'active' }
        await axios.patch(endpoint, payload, { withCredentials: true })
      }
      
      onRefresh()
      
    } catch (err) {
      alert(`Action failed: ${err.response?.data?.detail || err.message}`)
    } finally {
      setActionLoading(false)
    }
  }

  const formatLastActive = (lastActive) => {
    if (!lastActive) return 'Never'
    const date = new Date(lastActive)
    const now = new Date()
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    return date.toLocaleDateString()
  }

  return (
    <>
      <div className={`card p-4 hover:shadow-md transition-shadow mobile-full-width ${selected ? 'ring-2 ring-primary border-primary' : ''}`}>
        <div className="card-mobile-stack mb-3 mobile-full-width">
          <div className="flex items-center gap-3 flex-1 mobile-full-width">
            <input
              type="checkbox"
              className="rounded"
              checked={selected}
              onChange={onSelect}
            />
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-sm font-medium text-primary">
                {member.name.split(' ').map(n => n[0]).join('').toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0 mobile-full-width">
              <h3 className="font-medium text-primary truncate">{member.name}</h3>
              <p className="text-sm text-muted truncate">{member.email}</p>
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="icon-btn" disabled={actionLoading} aria-label={`Actions for team member ${member.name}`}>
                <MoreVertical size={16} aria-hidden="true" />
                <VisuallyHidden>
                  Open actions menu for {member.name} - {member.email}
                </VisuallyHidden>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShowProfileModal(true)}>
                View Profile
                <VisuallyHidden>View detailed profile for {member.name}</VisuallyHidden>
              </DropdownMenuItem>
              
              {/* Only show Change Role and Deactivate options for non-Super Admin users */}
              {member.role_key !== 'super_admin' ? (
                <>
                  <DropdownMenuItem onClick={() => setShowRoleModal(true)}>
                    Change Role
                    <VisuallyHidden>Modify role permissions for {member.name}</VisuallyHidden>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {member.status === 'active' ? (
                    <DropdownMenuItem 
                      onClick={() => performMemberAction('deactivate')}
                      className="text-red-600"
                    >
                      <UserX size={14} className="mr-2" aria-hidden="true" />
                      Deactivate
                      <VisuallyHidden>Deactivate {member.name}'s account access</VisuallyHidden>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem 
                      onClick={() => performMemberAction('reactivate')}
                      className="text-green-600"
                    >
                      <UserCheck size={14} className="mr-2" aria-hidden="true" />
                      Reactivate
                      <VisuallyHidden>Reactivate {member.name}'s account access</VisuallyHidden>
                    </DropdownMenuItem>
                  )}
                </>
              ) : (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled className="text-muted">
                    <UserCheck size={14} className="mr-2" aria-hidden="true" />
                    Platform Owner Protected
                    <VisuallyHidden>Super Admin account is protected and cannot be modified</VisuallyHidden>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="space-y-2 mobile-full-width">
          <div className="flex items-center gap-2 flex-wrap mobile-full-width">
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-primary-100 text-primary">
              {member.role_name}
            </span>
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${getHierarchyBadge(member.hierarchy_level)}`}>
              Level {member.hierarchy_level}
            </span>
          </div>

          <div className="flex items-center justify-between text-xs text-muted flex-wrap gap-1 mobile-full-width">
            <span className={getStatusColor(member.status)}>
              {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
            </span>
            <span className="text-right">
              Last: {formatLastActive(member.last_active)}
            </span>
          </div>

          {member.mfa_enabled && (
            <div className="flex items-center gap-1 text-xs text-green-600">
              <UserCheck size={12} />
              MFA Enabled
            </div>
          )}
        </div>
      </div>
      
      {/* View Profile Modal */}
      {showProfileModal && (
        <ViewProfileModal
          member={member}
          onClose={() => setShowProfileModal(false)}
        />
      )}
      
      {/* Role Change Modal */}
      {showRoleModal && (
        <RoleChangeModal
          member={member}
          onClose={() => setShowRoleModal(false)}
          onSuccess={() => {
            setShowRoleModal(false)
            onRefresh()
          }}
        />
      )}
    </>
  )
}

// Role Change Modal Component
function RoleChangeModal({ member, onClose, onSuccess }) {
  const [roles, setRoles] = useState([])
  const [selectedRoleId, setSelectedRoleId] = useState(member.role_id)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [justification, setJustification] = useState('')

  // Prevent role change modal for Super Admin
  if (member.role_key === 'super_admin') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-canvas rounded-lg p-6 w-full max-w-md">
          <h3 className="text-xl font-semibold text-primary mb-4">Change Role</h3>
          <div className="mb-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-3">
                <UserCheck size={20} className="text-red-600" />
                <div>
                  <h4 className="font-medium text-red-800">Platform Owner Protected</h4>
                  <p className="text-sm text-red-600 mt-1">
                    Super Admin role cannot be changed for security reasons. This account is the platform owner and is protected from modification.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <button onClick={onClose} className="btn secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Load available roles
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const { data } = await axios.get(`${API}/rbac/roles`, { withCredentials: true })
        // Filter out subscriber role as it shouldn't be assignable via team management
        const teamRoles = data.filter(role => role.key !== 'subscriber')
        setRoles(teamRoles)
      } catch (err) {
        setError('Failed to load available roles')
        console.error('Failed to load roles:', err)
      }
    }
    loadRoles()
  }, [])

  const handleRoleChange = async (e) => {
    e.preventDefault()
    if (!selectedRoleId || !justification.trim()) {
      setError('Please select a role and provide justification')
      return
    }

    setLoading(true)
    setError('')

    try {
      await axios.patch(
        `${API}/admin/team/members/${member.id}`,
        {
          role_id: selectedRoleId,
          justification: justification.trim()
        },
        { withCredentials: true }
      )
      
      onSuccess()
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update member role')
    } finally {
      setLoading(false)
    }
  }

  const selectedRole = roles.find(role => role.id === selectedRoleId)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-canvas rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-semibold text-primary mb-4">Change Role</h3>
        
        <div className="mb-4">
          <p className="text-sm text-muted mb-2">
            Changing role for: <strong>{member.name}</strong>
          </p>
          <p className="text-xs text-muted">
            Current role: {member.role_name} (Level {member.hierarchy_level})
          </p>
        </div>
        
        <form onSubmit={handleRoleChange} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-2">
              New Role
            </label>
            <select
              className="input w-full"
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              required
            >
              <option value="">Select a role...</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.name} (Level {role.hierarchy_level})
                </option>
              ))}
            </select>
            {selectedRole && (
              <p className="text-xs text-muted mt-1">
                {selectedRole.description}
              </p>
            )}
          </div>

          {selectedRole && (
            <div className="p-3 bg-muted-50 rounded-lg">
              <h4 className="text-sm font-medium text-primary mb-2">Role Details</h4>
              <div className="text-xs text-muted space-y-1">
                <p><strong>Hierarchy Level:</strong> {selectedRole.hierarchy_level}</p>
                <p><strong>Permissions:</strong> {selectedRole.permissions?.length || 0} permissions assigned</p>
                {selectedRole.hierarchy_level !== member.hierarchy_level && (
                  <p className={`font-medium ${selectedRole.hierarchy_level < member.hierarchy_level ? 'text-red-600' : 'text-green-600'}`}>
                    {selectedRole.hierarchy_level < member.hierarchy_level 
                      ? '↑ Higher authority level' 
                      : '↓ Lower authority level'
                    }
                  </p>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-primary mb-2">
              Justification <span className="text-red-500">*</span>
            </label>
            <textarea
              className="input w-full"
              rows="3"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Explain the reason for this role change..."
              required
            />
            <p className="text-xs text-muted mt-1">
              This will be recorded in the audit log for compliance purposes.
            </p>
          </div>

          {error && (
            <div className="error" role="alert">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="btn primary flex-1"
              disabled={loading || !selectedRoleId || !justification.trim()}
            >
              {loading ? 'Updating...' : 'Update Role'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn secondary flex-1"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// View Profile Modal Component
function ViewProfileModal({ member, onClose }) {
  const [detailedMember, setDetailedMember] = useState(member)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Load detailed member information
  useEffect(() => {
    const loadDetailedMember = async () => {
      try {
        setLoading(true)
        setError('')
        const { data } = await axios.get(`${API}/admin/team/members/${member.id}`, { withCredentials: true })
        setDetailedMember(data)
      } catch (err) {
        setError('Failed to load detailed member information')
        console.error('Failed to load member details:', err)
      } finally {
        setLoading(false)
      }
    }
    
    loadDetailedMember()
  }, [member.id])

  const formatLastActive = (lastActive) => {
    if (!lastActive) return 'Never'
    const date = new Date(lastActive)
    const now = new Date()
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    return date.toLocaleDateString()
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set'
    const date = new Date(dateString)
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString()
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50 border-green-200'
      case 'invited': return 'text-blue-600 bg-blue-50 border-blue-200'
      case 'deactivated': return 'text-red-600 bg-red-50 border-red-200'
      default: return 'text-muted bg-muted-50 border-muted-200'
    }
  }

  const getHierarchyBadge = (level) => {
    if (level <= 2) return 'bg-red-100 text-red-800 border-red-200'
    if (level <= 5) return 'bg-orange-100 text-orange-800 border-orange-200'
    if (level <= 7) return 'bg-blue-100 text-blue-800 border-blue-200'
    return 'bg-gray-100 text-gray-800 border-gray-200'
  }

  const groupPermissions = (permissions) => {
    const groups = {}
    permissions.forEach(permission => {
      const [module, action] = permission.split(':')
      if (!groups[module]) groups[module] = []
      groups[module].push(action)
    })
    return groups
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-canvas rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-muted-200">
          <h3 className="text-xl font-semibold text-primary">Team Member Profile</h3>
          <button 
            onClick={onClose}
            className="icon-btn"
            aria-label="Close profile modal"
          >
            <X size={18} aria-hidden="true" />
            <VisuallyHidden>Close team member profile</VisuallyHidden>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-muted">Loading detailed profile...</div>
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="error" role="alert">{error}</div>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-2xl font-medium text-primary">
                  {detailedMember.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </span>
              </div>
              <div className="flex-1">
                <h4 className="text-2xl font-medium text-primary mb-1">{detailedMember.name}</h4>
                <p className="text-muted text-lg mb-2">{detailedMember.email}</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(detailedMember.status)}`}>
                    {detailedMember.status.charAt(0).toUpperCase() + detailedMember.status.slice(1)}
                  </span>
                  {detailedMember.role_key === 'super_admin' && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                      Platform Owner
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Role and Hierarchy Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Role Information</label>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted">Role Name:</span>
                      <span className="text-sm font-medium">{detailedMember.role_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted">Role Key:</span>
                      <span className="text-sm font-mono bg-muted-50 px-2 py-1 rounded">{detailedMember.role_key}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted">Hierarchy Level:</span>
                      <span className={`text-sm font-medium px-2 py-1 rounded border ${getHierarchyBadge(detailedMember.hierarchy_level)}`}>
                        Level {detailedMember.hierarchy_level}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Account Details</label>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted">Member ID:</span>
                      <span className="text-xs font-mono bg-muted-50 px-2 py-1 rounded">{detailedMember.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted">Created:</span>
                      <span className="text-sm">{formatDate(detailedMember.created_at)}</span>
                    </div>
                    {detailedMember.location && (
                      <div className="flex justify-between">
                        <span className="text-sm text-muted">Location:</span>
                        <span className="text-sm">{detailedMember.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Security & Activity</label>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted">MFA Status:</span>
                      <span className={`text-sm font-medium ${detailedMember.mfa_enabled ? 'text-green-600' : 'text-red-600'}`}>
                        {detailedMember.mfa_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted">Last Active:</span>
                      <span className="text-sm">{formatLastActive(detailedMember.last_active)}</span>
                    </div>
                  </div>
                </div>

                {detailedMember.hierarchy_path && detailedMember.hierarchy_path.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">Hierarchy Path</label>
                    <div className="flex flex-wrap gap-1">
                      {detailedMember.hierarchy_path.map((path, index) => (
                        <span key={index} className="text-xs bg-muted-100 text-muted px-2 py-1 rounded font-mono">
                          {path}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Permissions */}
            {detailedMember.effective_permissions && detailedMember.effective_permissions.length > 0 && (
              <div className="border-t border-muted-200 pt-4">
                <label className="block text-sm font-medium text-primary mb-3">
                  Permissions ({detailedMember.effective_permissions.length})
                </label>
                <div className="max-h-48 overflow-y-auto">
                  {Object.entries(groupPermissions(detailedMember.effective_permissions)).map(([module, actions]) => (
                    <div key={module} className="mb-3">
                      <h6 className="text-xs font-medium text-primary mb-1 uppercase tracking-wide">
                        {module}
                      </h6>
                      <div className="flex flex-wrap gap-1">
                        {actions.map((action, index) => (
                          <span key={index} className="text-xs bg-primary-50 text-primary px-2 py-1 rounded border border-primary-200">
                            {action}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Close Actions */}
            <div className="border-t border-muted-200 pt-4">
              <div className="flex justify-end">
                <button 
                  onClick={onClose}
                  className="btn secondary"
                >
                  Close Profile
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TeamDirectory